/**
 * Status enums — the exact values from SRS Appendix A.
 * These are mirrored as Postgres CHECK constraints / Prisma enums; never
 * rename a value without a migration that rewrites existing rows.
 */
export const TENANT_STATUS = ['trial', 'active', 'past_due', 'suspended', 'archived'];
export const SUBSCRIPTION_STATUS = ['trialing', 'active', 'past_due', 'canceled'];
export const ISOLATION_MODE = ['shared_row', 'schema_per_tenant', 'database_per_tenant'];
export const PLATFORM_ROLE = [
    'owner',
    'platform_admin',
    'support_agent',
    'platform_finance',
    /**
     * Looks after a set of hospitals commercially: their tickets and their
     * renewals, and nothing else. Signs in to the client portal, never to the
     * owner console, and reaches no clinical data anywhere.
     */
    'contact_person',
];
/**
 * Roles an ordinary platform-staff account can hold.
 *
 * `owner` is deliberately absent. The Owner is not a row in `platform_users`
 * at all — it is its own table, its own sign-in (email + authenticator, no
 * password) and its own token audience, so there is nothing here to create one
 * with and no way to promote an admin into one.
 */
export const PLATFORM_STAFF_ROLE = [
    'platform_admin',
    'support_agent',
    'platform_finance',
    'contact_person',
];
/** Platform roles that may operate the owner console. */
export const OWNER_CONSOLE_ROLES = [
    'owner',
    'platform_admin',
    'support_agent',
    'platform_finance',
];
export const TICKET_STATUS = ['open', 'in_progress', 'escalated', 'resolved', 'closed'];
export const TICKET_PRIORITY = ['low', 'normal', 'high', 'urgent'];
/** Who wrote a ticket or a reply — the three audiences that can reach one. */
export const TICKET_AUTHOR_KIND = ['tenant_user', 'contact_person', 'platform_user'];
export const RENEWAL_REQUEST_STATUS = ['pending', 'approved', 'rejected'];
export const PLATFORM_ATTACHMENT_OWNER = ['ticket', 'ticket_message', 'renewal_request'];
export const PLATFORM_UPLOADER_KIND = ['tenant_user', 'platform_user'];
export const AUDIT_ACTION = ['create', 'update', 'delete', 'view_sensitive', 'break_glass', 'login', 'logout', 'export'];
export const PATIENT_CATEGORY = ['general', 'panel', 'insurance', 'staff', 'charity', 'vip'];
export const SEX = ['male', 'female', 'other', 'unknown'];
export const BLOOD_GROUP = ['a_pos', 'a_neg', 'b_pos', 'b_neg', 'ab_pos', 'ab_neg', 'o_pos', 'o_neg', 'unknown'];
/**
 * Who the guardian is, relative to the patient. `self` means the patient
 * manages their own visits and consent — the common case for an adult, and
 * the one a plain "Relation" free-text field used to have no way to say.
 */
export const GUARDIAN_RELATION = ['self', 'parent', 'child', 'spouse', 'sibling', 'other'];
export const APPOINTMENT_STATUS = ['booked', 'confirmed', 'arrived', 'in_consultation', 'completed', 'no_show', 'cancelled'];
export const APPOINTMENT_TYPE = ['new', 'follow_up', 'internal_referral'];
export const APPOINTMENT_CHANNEL = ['desk', 'portal', 'call_center', 'walk_in'];
export const ENCOUNTER_CONTEXT = ['opd', 'ipd', 'er', 'tele'];
export const ENCOUNTER_STATUS = ['open', 'closed'];
export const PRESCRIPTION_STATUS = ['issued', 'partially_dispensed', 'dispensed', 'cancelled'];
export const CLINICAL_ORDER_TYPE = ['lab', 'radiology', 'procedure', 'referral', 'admission', 'diet', 'nursing'];
export const CLINICAL_ORDER_STATUS = ['ordered', 'in_progress', 'completed', 'cancelled'];
export const ADMISSION_STATUS = ['admitted', 'discharged', 'lama', 'referred', 'expired', 'absconded'];
export const BED_STATUS = ['vacant', 'occupied', 'reserved', 'cleaning', 'blocked'];
export const BED_CLASS = ['general', 'semi_private', 'private', 'deluxe', 'icu', 'ccu', 'nicu', 'hdu', 'isolation'];
export const MEDICATION_ADMIN_STATUS = ['due', 'given', 'missed', 'held', 'refused'];
export const OT_CASE_STATUS = ['scheduled', 'pre_op', 'in_theatre', 'recovery', 'completed', 'cancelled'];
export const TRIAGE_LEVEL = ['level_1', 'level_2', 'level_3', 'level_4', 'level_5'];
export const ER_DISPOSITION = ['discharged', 'admitted', 'referred', 'expired', 'lama', 'absconded'];
export const PRODUCT_TYPE = ['drug', 'consumable', 'asset', 'service_item'];
export const STOCK_MOVEMENT_TYPE = ['grn', 'sale', 'indent_issue', 'return', 'transfer_out', 'transfer_in', 'adjustment', 'write_off', 'purchase_return', 'grn_void'];
export const STORE_TYPE = ['main_pharmacy', 'er_pharmacy', 'ot_pharmacy', 'ward_satellite', 'general_store'];
export const INDENT_STATUS = ['requested', 'approved', 'issued', 'partially_issued', 'rejected'];
export const PURCHASE_ORDER_STATUS = ['draft', 'submitted', 'partially_received', 'received', 'cancelled', 'closed'];
export const LAB_ORDER_ITEM_STATUS = ['ordered', 'collected', 'received', 'rejected', 'in_process', 'resulted', 'verified', 'released', 'cancelled'];
export const LAB_RESULT_FLAG = ['normal', 'low', 'high', 'abnormal', 'no_range', 'critical_low', 'critical_high'];
export const BILLING_EXEMPTION = ['credit', 'staff', 'free'];
export const HOME_COLLECTION_STATUS = ['requested', 'assigned', 'collected', 'failed'];
export const RADIOLOGY_MODALITY = ['xray', 'ultrasound', 'ct', 'mri', 'mammography', 'fluoroscopy', 'echo', 'ecg', 'eeg', 'endoscopy'];
export const RADIOLOGY_STATUS = ['ordered', 'scheduled', 'arrived', 'scanned', 'reported', 'verified', 'released', 'cancelled'];
export const INVOICE_CONTEXT = ['opd', 'ipd', 'pharmacy', 'lab', 'radiology', 'er'];
export const INVOICE_STATUS = ['draft', 'issued', 'partially_paid', 'paid', 'cancelled', 'refunded'];
export const PAYMENT_MODE = ['cash', 'card', 'bank_transfer', 'mobile_wallet', 'credit_panel', 'online_gateway', 'deposit'];
export const DEPOSIT_PAYMENT_MODE = ['cash', 'card', 'bank_transfer', 'mobile_wallet', 'online_gateway'];
export const COLLECTABLE_PAYMENT_MODE = ['cash', 'card', 'bank_transfer', 'mobile_wallet', 'credit_panel', 'online_gateway'];
export const PAYMENT_STATUS = ['pending', 'completed', 'failed', 'reverted'];
export const REFUND_STATUS = ['requested', 'approved', 'paid', 'rejected'];
export const DEPOSIT_TYPE = ['advance', 'top_up', 'refund', 'applied'];
export const MANUAL_DEPOSIT_TYPE = ['advance', 'top_up', 'refund'];
export const TXN_REF_PAYMENT_MODE = ['card', 'bank_transfer', 'mobile_wallet', 'online_gateway'];
export const CREDIT_NOTE_STATUS = ['requested', 'applied', 'rejected'];
export const CREDIT_NOTE_KIND = ['lines', 'amount'];
export const CREDIT_NOTE_SETTLEMENT = ['none', 'refund', 'keep_credit'];
export const CLAIM_STATUS = ['draft', 'submitted', 'queried', 'approved', 'partially_paid', 'paid', 'rejected', 'cancelled'];
export const DOCTOR_PAYOUT_STATUS = ['draft', 'approved', 'paid'];
export const NOTIFICATION_CHANNEL = ['sms', 'whatsapp', 'email', 'push'];
export const NOTIFICATION_STATUS = ['queued', 'sent', 'delivered', 'failed'];
export const TASK_STATUS = ['pending', 'in_progress', 'done', 'cancelled'];
export const NUMBER_SEQUENCE_TYPE = ['mrn', 'invoice', 'token', 'accession', 'admission', 'claim', 'grn', 'purchase_order', 'indent', 'receipt', 'journal', 'employee', 'therapy', 'online_order', 'credit_note', 'supplier_payment', 'purchase_return', 'stock_take', 'stock_adjustment', 'er_case', 'estimate', 'prescription', 'certificate', 'payer_invoice', 'blood_donor', 'blood_unit', 'maintenance_ticket'];
export const ENTITLEMENT_STATUS = ['enabled', 'disabled', 'retired'];
export const ENTITLEMENT_TOGGLE = ['enabled', 'disabled'];
export const ENTITLEMENT_SOURCE = ['plan', 'override'];
// ── Phase 4: insurance, accounting, HR and MRD ───────────────────────────────
export const PAYER_KIND = ['insurance', 'corporate', 'government', 'ngo'];
export const PREAUTH_STATUS = ['requested', 'approved', 'partially_approved', 'rejected', 'expired', 'cancelled'];
export const PREAUTH_RESPONSE_STATUS = ['requested', 'approved', 'partially_approved', 'rejected', 'expired'];
export const EXCHANGE_STATE = ['unsent', 'queued', 'sent', 'failed'];
export const PREAUTH_KIND = ['new', 'update', 'extension', 'resubmission'];
export const ADJUDICATION_STATUS = ['pending', 'approved', 'partially_approved', 'rejected', 'pended', 'cancelled', 'not_required'];
export const CLAIM_SETTLEMENT_DECISION = ['write_off', 'bill_patient', 'credit_patient', 'resubmit', 'dispute'];
export const CLAIM_LINE_SOURCE = ['nphies', 'manual'];
export const SHARE_SOURCE = ['preauth', 'benefit', 'plan', 'coverage', 'payer', 'settlement'];
export const RECONCILIATION_STATUS = ['received', 'posted', 'unmatched', 'needs_review', 'reversed'];
export const PAYMENT_RECONCILIATION_STATUS = ['received', 'allocated', 'partially_allocated', 'unmatched', 'needs_review'];
export const PAYMENT_NOTICE_STATE = ['none', 'queued', 'sent', 'failed', 'not_applicable'];
export const COMMUNICATION_STATUS = ['open', 'replied', 'closed', 'expired'];
export const COMMUNICATION_PRIORITY = ['routine', 'urgent', 'asap', 'stat'];
export const POLL_STATUS = ['never', 'ok', 'failed', 'offline'];
/** The five statement roots. Every account hangs off one of these. */
export const ACCOUNT_TYPE = ['asset', 'liability', 'equity', 'income', 'expense'];
export const LEAVE_TYPE = ['casual', 'sick', 'annual', 'unpaid', 'maternity', 'paternity'];
export const LEAVE_STATUS = ['pending', 'approved', 'rejected', 'cancelled'];
export const ATTENDANCE_STATUS = ['present', 'absent', 'leave', 'half_day', 'holiday', 'off'];
export const PAYROLL_STATUS = ['draft', 'processed', 'paid'];
export const RECORD_REQUEST_STATUS = ['pending', 'approved', 'released', 'rejected'];
// ── Phase 5: blood bank, support services and portals ────────────────────────
export const BLOOD_COMPONENT = ['whole_blood', 'prbc', 'ffp', 'platelets', 'cryoprecipitate'];
export const BLOOD_UNIT_STATUS = ['quarantine', 'available', 'reserved', 'issued', 'transfused', 'discarded', 'expired'];
export const CROSSMATCH_RESULT = ['pending', 'compatible', 'incompatible', 'issued', 'transfused', 'cancelled'];
export const CROSSMATCH_RECORDED_RESULT = ['compatible', 'incompatible', 'cancelled'];
export const COMPLAINT_STATUS = ['open', 'in_progress', 'resolved', 'closed', 'escalated'];
export const HOUSEKEEPING_TYPE = ['bed_cleaning', 'linen_change', 'deep_clean', 'spill', 'waste'];
export const HOUSEKEEPING_STATUS = ['pending', 'in_progress', 'done', 'cancelled'];
export const DIET_TYPE = ['normal', 'diabetic', 'renal', 'cardiac', 'soft', 'liquid', 'npo', 'high_protein', 'low_salt'];
export const DIET_ORDER_STATUS = ['active', 'stopped', 'completed'];
// ── Public directory (patient-facing marketplace) ────────────────────────────
export const FACILITY_TYPE = ['hospital', 'lab', 'pharmacy'];
export const REQUEST_KIND = ['opd', 'lab'];
export const REQUEST_STATUS = ['pending', 'approved', 'rejected', 'cancelled'];
export const ONLINE_ORDER_STATUS = ['pending', 'approved', 'rejected', 'dispensed', 'cancelled'];
export const ONLINE_PAYMENT_METHOD = ['cod', 'transfer'];
//# sourceMappingURL=enums.js.map